import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Check, Zap, User, Tv2, Calendar,
  IndianRupee, Tag, Loader2, CreditCard, Receipt,
  Plus, X, ShieldCheck, Info, TrendingDown, AlertTriangle, Clock, Wallet,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SearchableSelect from "@/components/ui/SearchableSelect";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;

/* ─── Dealer Processing Screen (polls until webhook fulfills) ─────────── */
function DealerProcessingScreen({ rechargeId, onDone }) {
  const [dots, setDots] = useState(".");
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const dotInterval = setInterval(() => setDots(d => d.length >= 3 ? "." : d + "."), 500);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);

    const poll = async () => {
      if (Date.now() - startRef.current > POLL_TIMEOUT_MS) {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        clearInterval(dotInterval);
        onDone({ status: "recharge_failed", ok: false, message: "Recharge timed out. Please contact admin." });
        return;
      }
      try {
        const { data } = await api.get(`/dealer/recharge/${rechargeId}/status`);
        if (data.status === "success" || data.status === "recharge_failed" || data.status === "failed") {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          clearInterval(dotInterval);
          onDone(data);
        }
      } catch (_) { }
    };

    setTimeout(poll, 2000);
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); clearInterval(dotInterval); };
  }, [rechargeId, onDone]);

  return (
    <div className="text-center py-12 space-y-6">
      <div className="relative w-28 h-28 mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-brand-primary/20" />
        <div className="absolute inset-0 rounded-full border-4 border-t-brand-primary animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Zap className="w-10 h-10 text-brand-primary" />
        </div>
      </div>
      <div>
        <h2 className="font-heading font-bold text-2xl text-brand-nav">Processing Recharge{dots}</h2>
        <p className="text-slate-500 mt-2 text-sm max-w-xs mx-auto">
          Payment received! Activating the customer's DTH subscription...
        </p>
      </div>
      <div className="max-w-xs mx-auto">
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full transition-all duration-1000"
            style={{ width: `${Math.min(95, (elapsed / (POLL_TIMEOUT_MS / 1000)) * 100 + 5)}%` }} />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>Payment verified</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {elapsed}s</span>
        </div>
      </div>
      <p className="text-xs text-slate-400">Please don't close this page.</p>
    </div>
  );
}

/* ─── Razorpay Script Loader ──────────────────────────────────────────── */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const fmt = (v) => `₹${Number(v || 0).toFixed(2)}`;

// NOTE: recharge_amount (API amount) is NEVER shown to dealer.
// Dealer only sees: MRP (base_cost) and Dealer Price.

const STEPS = ["Customer", "Plan", "Confirm", "Done"];

/* ─── Step Progress Bar ───────────────────────────────────────────────────── */
function StepBar({ step }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step
                  ? "bg-green-500 text-white shadow-sm"
                  : i === step
                    ? "bg-brand-primary text-white shadow-md ring-4 ring-brand-primary/20"
                    : "bg-slate-100 text-slate-400"
                }`}
            >
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1 font-semibold ${i === step ? "text-brand-primary" : "text-slate-400"}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-0.5 w-10 mb-4 rounded ${i < step ? "bg-green-400" : "bg-slate-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─── Plan Card (dealer view: MRP + Dealer Price only) ────────────────────── */
function PlanCard({ p, selected, onSelect }) {
  const isSelected = selected?.id === p.id;
  const dealerPriceVal = p.dealer_price > 0 ? p.dealer_price : p.base_cost;
  return (
    <button
      onClick={() => onSelect(p)}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all ${isSelected
          ? "border-brand-primary bg-brand-primary/5 shadow-md"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
        }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">{p.operator}</span>
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">{p.category}</span>
          </div>
          <p className="font-semibold text-brand-nav truncate">{p.name}</p>
          {p.description && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{p.description}</p>
          )}
          <div className="flex gap-3 mt-2 flex-wrap">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {p.validity_days}d
            </span>
            {p.channel_count > 0 && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Tv2 className="w-3 h-3" /> {p.channel_count} ch
              </span>
            )}
          </div>
        </div>
        {/* Dealer sees MRP and Dealer Price — NO recharge_amount */}
        <div className="text-right shrink-0 space-y-1.5">
          <div>
            <p className="text-[10px] text-slate-400 font-semibold uppercase">MRP</p>
            <p className="font-heading font-black text-xl text-slate-800">{fmt(p.base_cost)}</p>
          </div>
          <div>
            <p className="text-[10px] text-green-600 font-semibold uppercase">Dealer Price</p>
            <p className="font-bold text-green-700 text-sm">{fmt(dealerPriceVal)}</p>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ─── Payment Preview Popup ───────────────────────────────────────────────── */
function PaymentPreviewModal({ plan, connection, customer, addonCharge, extraDiscount, onConfirm, onClose, submitting }) {
  const baseCost = plan?.base_cost || 0;
  const addon = parseFloat(addonCharge) || 0;
  const disc = parseFloat(extraDiscount) || 0;
  const mrp = baseCost + addon; // merged — customer sees this as "Plan MRP"
  const total = mrp - disc;     // discount applied on merged MRP

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        style={{ border: "1px solid #e2e8f0" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-primary" />
            <h3 className="font-heading font-bold text-lg text-brand-nav">Payment Preview</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Customer & connection */}
          <div className="flex justify-between text-sm bg-slate-50 rounded-xl px-4 py-3">
            <div>
              <p className="text-xs text-slate-400">Customer</p>
              <p className="font-semibold text-brand-nav">{customer?.name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">DTH ID</p>
              <p className="font-semibold font-mono text-brand-nav">{connection?.dth_id}</p>
              <p className="text-xs text-slate-400">{connection?.operator}</p>
            </div>
          </div>

          {/* Plan */}
          <div className="bg-slate-50 rounded-xl px-4 py-3">
            <p className="text-xs text-slate-400 mb-1">Plan</p>
            <p className="font-semibold text-brand-nav">{plan?.name}</p>
            <p className="text-xs text-slate-400">{plan?.operator} · {plan?.category} · {plan?.validity_days}d</p>
          </div>

          {/* Price breakdown — shows MRP and discount. Add-on is HIDDEN but included in total */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 flex justify-between text-sm border-b border-slate-100">
              <span className="text-slate-500 flex items-center gap-1.5">
                <Tag className="w-3 h-3" /> Plan MRP
              </span>
              <span className="font-semibold text-slate-700">{fmt(mrp)}</span>
            </div>
            {disc > 0 && (
              <div className="px-4 py-3 flex justify-between text-sm border-b border-slate-100 bg-green-50">
                <span className="text-green-700 flex items-center gap-1.5">
                  <TrendingDown className="w-3 h-3" /> Discount
                </span>
                <span className="font-semibold text-green-700">− {fmt(disc)}</span>
              </div>
            )}
            {/* Add-on line is intentionally NOT shown here — it's silently added to total */}
            <div
              className="px-4 py-4 flex justify-between items-center"
              style={{ background: "linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)" }}
            >
              <div>
                <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest">Total to Collect</p>
                <p className="text-xs text-indigo-300 mt-0.5">Amount from customer</p>
              </div>
              <p className="font-heading font-black text-3xl text-white">{fmt(total)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-xl transition disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
            ) : (
              <><CreditCard className="w-4 h-4" /> Confirm & Pay {fmt(total)}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────────────── */
export default function InitiateRecharge() {
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [rechargeId, setRechargeId] = useState(null);
  const [feeInfo, setFeeInfo] = useState({ gateway_fee_percent: 0, gateway_fee_bearer: 'user' });

  const [selectedEntry, setSelectedEntry] = useState(null); // {customer, connection}
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [addonCharge, setAddonCharge] = useState("");
  const [extraDiscount, setExtraDiscount] = useState("");
  const [paymentMode, setPaymentMode] = useState("razorpay"); // razorpay | wallet
  const [walletBalance, setWalletBalance] = useState(0);

  const [customerSearch, setCustomerSearch] = useState("");
  const [planSearch, setPlanSearch] = useState("");
  const [planCategory, setPlanCategory] = useState("All");

  useEffect(() => {
    Promise.all([
      api.get("/dealer/recharge/customers"),
      api.get("/dealer/catalog?purpose=recharge"),
      api.get("/dealer/gateway-fee-info"),
      api.get("/dealer/wallet"),
    ])
      .then(([c, p, f, w]) => { setCustomers(c.data); setCatalog(p.data); setFeeInfo(f.data); setWalletBalance(w.data?.balance || 0); })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  /* Re-fetch catalog filtered by selected customer's connection group */
  const loadCatalogForConnection = (connectionId) => {
    const url = connectionId
      ? `/dealer/catalog?purpose=recharge&connection_id=${connectionId}`
      : "/dealer/catalog?purpose=recharge";
    api.get(url).then(r => setCatalog(r.data)).catch(() => { });
  };

  const go = (n) => setStep(n);
  const back = () => setStep((s) => s - 1);

  const handleRecharge = async () => {
    if (!selectedEntry || !selectedPlan) return;
    setSubmitting(true);
    try {
      // Step 1: Create Razorpay order
      const { data } = await api.post("/dealer/recharge", {
        customer_id_fk: selectedEntry.connection.id,
        product_id: selectedPlan.id,
        extra_discount: parseFloat(extraDiscount) || 0,
        addon_charge: parseFloat(addonCharge) || 0,
      });

      if (!data.ok || data.status !== "pending") {
        toast.error("Failed to create payment order");
        setSubmitting(false);
        return;
      }

      // Step 2: Open Razorpay checkout
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Payment gateway failed to load.");
        setSubmitting(false);
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "DTH Recharge",
        description: `${selectedPlan.name} - ${selectedPlan.validity_days} days` +
          (data.gateway_fee > 0 ? ` (incl. ${data.gateway_fee_percent}% gateway fee)` : ''),
        handler: async (response) => {
          // Step 3: Verify signature only — DishTV triggered by webhook
          try {
            const verifyRes = await api.post("/dealer/recharge/verify", {
              recharge_id: data.recharge_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data.ok) {
              setRechargeId(data.recharge_id);
              setShowPreview(false);
              go(3); // processing screen — start polling
              toast.success("Payment confirmed! Activating DTH...");
            } else if (verifyRes.data.status === 'recharge_failed') {
              // Payment was OK but DishTV recharge failed — show real reason
              setRechargeId(data.recharge_id);
              setShowPreview(false);
              go(3); // go to polling screen so user can see the failure details
              toast.error(verifyRes.data.message || "Recharge failed. Our team will review.");
            } else {
              toast.error(verifyRes.data.message || "Payment verification failed. Please contact support.");
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Verification failed");
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.info("Payment cancelled");
          },
        },
        theme: { color: "#6366f1" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setSubmitting(false);
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recharge failed");
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setSelectedEntry(null);
    setSelectedPlan(null);
    setAddonCharge("");
    setExtraDiscount("");
    setResult(null);
    setRechargeId(null);
    setShowPreview(false);
    setPaymentMode("razorpay");
    // Refresh wallet balance
    api.get("/dealer/wallet").then(r => setWalletBalance(r.data?.balance || 0)).catch(() => { });
  };

  const handlePollDone = (data) => {
    setResult(data);
    go(4); // final result screen
    if (data.status === "success") toast.success("Recharge done! Subscription activated.");
    else toast.error("Recharge failed. Please contact admin.");
  };

  /* ── Wallet Recharge Handler ── */
  const handleWalletRecharge = async () => {
    if (!selectedEntry || !selectedPlan) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/dealer/recharge/wallet", {
        customer_id_fk: selectedEntry.connection.id,
        product_id: selectedPlan.id,
        extra_discount: parseFloat(extraDiscount) || 0,
        addon_charge: parseFloat(addonCharge) || 0,
      });
      setShowPreview(false);
      if (data.ok) {
        setWalletBalance(data.wallet_balance || 0);
        setResult({
          status: "success",
          plan_end: data.plan_end,
          message: data.dtv_message || "Recharge successful!",
        });
        go(4);
        toast.success("Wallet recharge done! Subscription activated.");
      } else {
        setResult({
          status: data.status || "recharge_failed",
          message: data.message || "Recharge failed.",
        });
        if (data.wallet_balance !== undefined) setWalletBalance(data.wallet_balance);
        go(4);
        if (data.refunded) {
          toast.error(`Recharge failed. ₹${data.amount_charged || totalAmount} refunded to wallet.`);
        } else {
          toast.error(data.message || "Recharge outcome unclear. Admin will review.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wallet recharge failed");
    } finally {
      setSubmitting(false);
    }
  };

  const categories = ["All", ...new Set(catalog.map((p) => p.category))];
  const filteredCatalog = catalog.filter((p) => {
    const q = planSearch.toLowerCase();
    return (
      (planCategory === "All" || p.category === planCategory) &&
      (!q || p.name.toLowerCase().includes(q) || p.operator.toLowerCase().includes(q))
    );
  });
  const filteredCustomers = customers.filter((c) => {
    const q = customerSearch.toLowerCase();
    return (
      !q ||
      c.customer?.name?.toLowerCase().includes(q) ||
      c.connection?.dth_id?.toLowerCase().includes(q)
    );
  });

  const addonVal = parseFloat(addonCharge) || 0;
  const discVal = parseFloat(extraDiscount) || 0;
  const maxDisc = selectedPlan?.discount || 0;
  const totalAmount = (selectedPlan?.base_cost || 0) - Math.min(discVal, maxDisc) + addonVal;

  // Gateway fee — only shown to user if bearer is 'user'
  const showFee = feeInfo.gateway_fee_bearer === 'user' && feeInfo.gateway_fee_percent > 0;
  const feeAmount = showFee ? Math.round(totalAmount * feeInfo.gateway_fee_percent) / 100 : 0;
  const grandTotal = Math.round((totalAmount + feeAmount) * 100) / 100;

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-brand-nav">Recharge for Customer</h3>
        <p className="text-sm text-slate-400 mt-0.5">
          Select customer → choose plan → add charges → confirm.
        </p>
      </div>

      <StepBar step={step} />

      {/* ── Step 0: Select Customer ── */}
      {step === 0 && (
        <div>
          <h4 className="font-semibold text-brand-nav mb-3">Select Customer</h4>
          <input
            type="text"
            placeholder="Search by name or DTH ID..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            className="w-full mb-3 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
            {filteredCustomers.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No customers under your account.</p>
            )}
            {filteredCustomers.map((entry, i) => (
              <button
                key={i}
                onClick={() => { setSelectedEntry(entry); loadCatalogForConnection(entry.connection?.id); go(1); }}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-primary hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-brand-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-brand-nav">{entry.customer?.name}</p>
                      <p className="text-xs text-slate-400">{entry.customer?.mobile}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-slate-600">{entry.connection?.operator}</p>
                    <p className="text-xs font-mono text-slate-400">ID: {entry.connection?.dth_id}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 1: Choose Plan ── */}
      {step === 1 && selectedEntry && (
        <div>
          <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {/* Customer/connection summary */}
          <div className="flex gap-3 mb-5">
            <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
              <p className="text-xs text-slate-400">Customer</p>
              <p className="font-semibold text-brand-nav">{selectedEntry.customer?.name}</p>
              <p className="text-xs text-slate-400">{selectedEntry.customer?.mobile}</p>
            </div>
            <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
              <p className="text-xs text-slate-400">DTH Connection</p>
              <p className="font-semibold font-mono text-brand-nav">{selectedEntry.connection?.dth_id}</p>
              <p className="text-xs text-slate-400">{selectedEntry.connection?.operator}</p>
            </div>
          </div>

          {/* Filter row */}
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              type="text"
              placeholder="Search plans..."
              value={planSearch}
              onChange={(e) => setPlanSearch(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none flex-1 min-w-0"
            />
            <div className="w-40">
              <SearchableSelect value={planCategory} onChange={setPlanCategory} options={categories.map(c => ({ label: c, value: c }))} />
            </div>
          </div>

          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {filteredCatalog.map((p) => (
              <PlanCard
                key={p.id}
                p={p}
                selected={selectedPlan}
                onSelect={(plan) => { setSelectedPlan(plan); go(2); }}
              />
            ))}
            {filteredCatalog.length === 0 && (
              <p className="text-slate-400 text-sm text-center py-8">No plans found.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Add-on + Confirm ── */}
      {step === 2 && selectedEntry && selectedPlan && (
        <div>
          <button onClick={back} className="flex items-center gap-1 text-sm text-brand-primary font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h4 className="font-semibold text-brand-nav mb-4">Review & Add Charges</h4>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-5 shadow-sm">
            {/* Customer row */}
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Customer / Connection</p>
              <div className="flex justify-between">
                <div>
                  <p className="font-semibold text-brand-nav">{selectedEntry.customer?.name}</p>
                  <p className="text-xs text-slate-400">{selectedEntry.customer?.mobile}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-brand-nav">{selectedEntry.connection?.operator}</p>
                  <p className="text-xs font-mono text-slate-400">{selectedEntry.connection?.dth_id}</p>
                </div>
              </div>
            </div>

            {/* Plan details */}
            <div className="px-5 py-4 border-b border-slate-100 space-y-2 text-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Plan</p>
              <div className="flex justify-between">
                <span className="text-slate-500">Name</span>
                <span className="font-semibold">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Operator / Category</span>
                <span>{selectedPlan.operator} · {selectedPlan.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Validity</span>
                <span>{selectedPlan.validity_days} days</span>
              </div>
            </div>

            {/* Pricing — dealer sees MRP and Dealer Price only */}
            <div className="px-5 py-4 border-b border-slate-100 space-y-2 text-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Pricing</p>
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1.5">
                  <Tag className="w-3 h-3" /> Plan MRP
                </span>
                <span className="font-semibold text-slate-700">{fmt(selectedPlan.base_cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1.5">
                  <IndianRupee className="w-3 h-3 text-green-500" /> Dealer Price
                </span>
                <span className="font-semibold text-green-600">
                  {fmt(selectedPlan.dealer_price > 0 ? selectedPlan.dealer_price : selectedPlan.base_cost)}
                </span>
              </div>
            </div>

            {/* Discount input */}
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                <TrendingDown className="w-3 h-3 inline mr-1 text-green-500" />
                Discount (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <input
                  type="number"
                  min="0"
                  max={selectedPlan?.discount || 0}
                  step="0.01"
                  placeholder="0.00"
                  value={extraDiscount}
                  onChange={(e) => setExtraDiscount(e.target.value)}
                  className="w-full pl-7 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40"
                />
              </div>
              {maxDisc > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  Max allowed: {fmt(maxDisc)} (plan discount limit)
                </p>
              )}
            </div>

            {/* Add-on charge input */}
            <div className="px-5 py-4 border-b border-slate-100">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                <Plus className="w-3 h-3 inline mr-1 text-amber-500" />
                Add-on Charge (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={addonCharge}
                  onChange={(e) => setAddonCharge(e.target.value)}
                  className="w-full pl-7 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Service/convenience fee. Hidden in payment preview but included in total.
              </p>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Subtotal</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    MRP{discVal > 0 ? ` − Discount ${fmt(Math.min(discVal, maxDisc))}` : ""}{addonVal > 0 ? ` + Add-on ${fmt(addonVal)}` : ""}
                  </p>
                </div>
                <p className="font-heading font-bold text-xl text-slate-700">{fmt(totalAmount)}</p>
              </div>
            </div>

            {/* Gateway Fee line — only if user bears the fee */}
            {showFee && (
              <div className="px-5 py-3 border-t border-dashed border-amber-200 bg-amber-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Transaction Fee ({feeInfo.gateway_fee_percent}%)</p>
                    <p className="text-[11px] text-amber-500 mt-0.5">Payment gateway processing charge</p>
                  </div>
                  <p className="font-heading font-bold text-lg text-amber-600">+ {fmt(feeAmount)}</p>
                </div>
              </div>
            )}

            {/* Grand Total */}
            <div className={`px-5 py-4 ${showFee ? 'border-t border-slate-200 bg-brand-primary/5' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total to Pay</p>
                </div>
                <p className="font-heading font-black text-3xl text-brand-primary">{fmt(grandTotal)}</p>
              </div>
            </div>
          </div>

          {/* Payment Mode Selector */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-5 shadow-sm">
            <div className="px-5 py-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Payment Method</p>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setPaymentMode('razorpay')}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${paymentMode === 'razorpay'
                      ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300'
                    }`}>
                  <CreditCard className={`w-5 h-5 ${paymentMode === 'razorpay' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${paymentMode === 'razorpay' ? 'text-indigo-700' : 'text-slate-600'}`}>Online Payment</p>
                    <p className="text-[10px] text-slate-400">Razorpay (UPI/Card)</p>
                  </div>
                </button>
                <button type="button" onClick={() => setPaymentMode('wallet')}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${paymentMode === 'wallet'
                      ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300'
                    }`}>
                  <Wallet className={`w-5 h-5 ${paymentMode === 'wallet' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${paymentMode === 'wallet' ? 'text-emerald-700' : 'text-slate-600'}`}>Wallet</p>
                    <p className="text-[10px] text-slate-400">Balance: ₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                </button>
              </div>
              {paymentMode === 'wallet' && walletBalance < grandTotal && (
                <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-600">
                    Insufficient wallet balance. Need ₹{grandTotal.toFixed(2)} but only ₹{walletBalance.toFixed(2)} available.
                    Please ask your admin to top up your wallet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preview & Confirm button */}
          {paymentMode === 'razorpay' ? (
            <button
              onClick={() => setShowPreview(true)}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-primary to-brand-accent text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              <ShieldCheck className="w-5 h-5" />
              Preview & Confirm Payment — {fmt(grandTotal)}
            </button>
          ) : (
            <button
              onClick={handleWalletRecharge}
              disabled={submitting || walletBalance < grandTotal}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
              ) : (
                <><Wallet className="w-5 h-5" /> Pay from Wallet — {fmt(totalAmount)}</>
              )}
            </button>
          )}
          <p className="text-center text-xs text-slate-400 mt-2">
            {paymentMode === 'razorpay'
              ? 'A payment summary popup will appear before final confirmation.'
              : 'Amount will be deducted from your wallet instantly.'}
          </p>
        </div>
      )}

      {/* ── Step 3: Processing (polling) ── */}
      {step === 3 && rechargeId && (
        <DealerProcessingScreen rechargeId={rechargeId} onDone={handlePollDone} />
      )}

      {/* ── Step 4: Final Result ── */}
      {step === 4 && result && (
        <div className="text-center space-y-5 py-8">
          {result.status === "success" ? (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-green-50 border-4 border-green-100 flex items-center justify-center">
                <Check className="w-12 h-12 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Recharge Done!</h2>
                <p className="text-slate-500 mt-1">Subscription activated successfully.</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-left space-y-3 max-w-sm mx-auto">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Customer</span>
                  <span className="font-semibold">{selectedEntry?.customer?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-semibold">{selectedPlan?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Active until</span>
                  <span className="font-semibold text-green-600">{result.plan_end}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-24 h-24 mx-auto rounded-full bg-amber-50 border-4 border-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-amber-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-brand-nav">Payment Received</h2>
                <p className="text-slate-500 mt-1">Recharge is being reviewed by admin.</p>
                <p className="text-sm text-amber-600 mt-2 bg-amber-50 rounded-lg px-4 py-2 inline-block">
                  {result.message || "We'll process your recharge shortly."}
                </p>
              </div>
            </>
          )}
          <button onClick={reset} className="text-sm font-semibold text-brand-primary hover:underline">
            <Receipt className="w-4 h-4 inline mr-1" />
            Recharge another →
          </button>
        </div>
      )}

      {/* ── Payment Preview Popup ── */}
      {showPreview && selectedEntry && selectedPlan && (
        <PaymentPreviewModal
          plan={selectedPlan}
          connection={selectedEntry.connection}
          customer={selectedEntry.customer}
          addonCharge={addonCharge}
          extraDiscount={Math.min(parseFloat(extraDiscount) || 0, maxDisc)}
          submitting={submitting}
          onConfirm={handleRecharge}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
